import React from "react";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import type { TriageIssue } from "../utils/deriveIssues";
import type { DrillClick } from "../utils/drilldown";
import { formatFailRate } from "../utils/chartFormatters";
import {
  ATTENTION_FAIL_RATE_WATCH,
  ATTENTION_SCORE_THRESHOLD,
} from "../constants";
import { Skeleton } from "../../../ui/Skeleton";

interface TriageRailProps {
  issues: TriageIssue[];
  loading?: boolean;
  /** Drills into the offending traces for an issue. */
  onDrill?: (click: DrillClick) => void;
  className?: string;
}

/**
 * Right-hand worklist that turns "something's wrong" into "here's what to fix":
 * the result groups failing too often (see deriveIssues) ranked worst-first,
 * each with a drill into its traces. Reflects the active grouping —
 * per-criterion by default, per-(criterion × segment) once a group-by is chosen.
 *
 * Each entry leads with its failure rate, because that is what put it on the
 * list; the average is context, and on its own it hides exactly the failures
 * this rail exists to surface (#1492).
 */
export function TriageRail({
  issues,
  loading = false,
  onDrill,
  className = "",
}: TriageRailProps) {
  const criticals = issues.filter((i) => i.severity === "critical").length;
  const watches = issues.length - criticals;

  return (
    <aside
      className={`bg-card rounded-lg shadow-sm border border-border p-4 ${className}`}
      data-testid="triage-rail"
    >
      <div className="flex items-center justify-between gap-2 mb-3">
        <h3 className="text-sm font-semibold uppercase tracking-wide">Triage</h3>
        {!loading && issues.length > 0 && (
          <div className="flex items-center gap-1.5 text-[11px] font-semibold font-mono">
            {criticals > 0 && (
              <span className="px-2 py-0.5 rounded-full bg-destructive/10 text-destructive">
                {criticals} critical
              </span>
            )}
            {watches > 0 && (
              <span className="px-2 py-0.5 rounded-full bg-warning/15 text-warning-foreground">
                {watches} watch
              </span>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex flex-col gap-2" data-testid="triage-loading">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} height={64} className="rounded-md" />
          ))}
        </div>
      ) : issues.length === 0 ? (
        <div
          className="flex flex-col items-center text-center py-8 px-2"
          data-testid="triage-healthy"
        >
          <CheckCircleIcon className="h-8 w-8 text-primary mb-2" />
          <p className="text-sm font-medium">Nothing needs attention</p>
          <p className="text-xs text-muted-foreground mt-1">
            Nothing is failing your {ATTENTION_SCORE_THRESHOLD.toFixed(2)}{" "}
            threshold more than {formatFailRate(ATTENTION_FAIL_RATE_WATCH)} of
            the time. Back to building.
          </p>
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {issues.map((issue) => {
            const isCritical = issue.severity === "critical";
            // Colour by severity (not the continuous score scale) so a critical
            // item always reads red and a watch item amber, matching the pills.
            const sevText = isCritical ? "text-destructive" : "text-warning-foreground";
            const sevBorder = isCritical
              ? "border-l-destructive"
              : "border-l-warning";
            return (
              <li
                key={issue.key}
                data-testid="triage-issue"
                data-severity={issue.severity}
                className={`rounded-md border border-border border-l-4 ${sevBorder} bg-background/40 p-3`}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-sm font-semibold truncate" title={issue.label}>
                    {issue.label}
                  </span>
                  <span
                    className={`text-sm font-mono font-semibold tabular-nums ${sevText}`}
                  >
                    {issue.failRate != null
                      ? formatFailRate(issue.failRate)
                      : issue.avg.toFixed(2)}
                  </span>
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5 tabular-nums">
                  {issue.failCount != null && (
                    <>
                      {issue.failCount.toLocaleString()} of{" "}
                      {issue.count.toLocaleString()} below{" "}
                      {ATTENTION_SCORE_THRESHOLD.toFixed(2)} ·{" "}
                    </>
                  )}
                  avg {issue.avg.toFixed(2)}
                </p>
                {onDrill && (
                  <button
                    type="button"
                    onClick={() => onDrill(issue.drill)}
                    className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                  >
                    View {issue.count.toLocaleString()} traces →
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </aside>
  );
}
